import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import DoneAllIcon from '@mui/icons-material/ClearAll';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Card from 'components/Card';
import { Col, Row } from 'components/Grid';
import Notification from 'containers/Notification';
import Page from 'containers/Page';
import PageMenu from 'containers/Page/Menu';
import PageMenuItem from 'containers/Page/MenuItem';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { GET_NOTIFICATIONS_INFO } from '../schema/apolloQueries/notificationsQueries';
import InfiniteScrollApp from '../components/InfiniteScrollerApollo';
import { GET_PROFILE_INFO } from '../schema/apolloQueries/commonQueries';
import {
  UPDATE_NOTIFICATION_READ,
  UPDATE_NOTIFICATION_READ_ALL,
} from '../schema/apolloMutations/notificationMutations';

const pageSize = 40;
const styles = {
  divider: { marginTop: 0 },
  iconButton: {
    padding: 0,
    width: null,
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

const Notifications = () => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { data, fetchMore } = useQuery(GET_NOTIFICATIONS_INFO, {
    variables: {
      pageNo: 1,
      pageSize,
    },
  });

  const [handleReadSignle] = useMutation(UPDATE_NOTIFICATION_READ, {
    refetchQueries: [
      GET_NOTIFICATIONS_INFO,
      {
        variables: {
          pageNo: 1,
          pageSize,
        },
      },
    ],
  });

  const [handleReadAll] = useMutation(UPDATE_NOTIFICATION_READ_ALL, {
    refetchQueries: [
      GET_NOTIFICATIONS_INFO,
      {
        variables: {
          pageNo: 1,
          pageSize,
        },
      },
    ],
  });

  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  useEffect(() => {
    if (Array.isArray(data?.getMyNotifications?.data)) {
      setIsFirstLoad(false);
    }
  }, [data]);

  const hasUnread = useMemo(() => {
    return profileData?.getProfile?.totalNotificationCount > 0;
  }, [profileData?.getProfile?.totalNotificationCount]);

  const markAllRead = useCallback(() => {
    handleReadAll({
      variables: {
        markAllRead: true,
      },
    });
  }, []);

  const handleFetchMore = (isPage, setHasMore) => {
    return new Promise((reslove, reject) => {
      fetchMore({
        variables: {
          pageNo: isPage,
          pageSize,
        },
        updateQuery: (pre, { fetchMoreResult }) => {
          if (
            pre?.getMyNotifications.data?.length
            >= fetchMoreResult?.getMyNotifications?.total
          ) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
          return pre?.getMyNotifications.data?.length
            >= fetchMoreResult?.getMyNotifications?.total
            ? pre
            : {
                getMyNotifications: {
                  __typename: pre.getMyNotifications.__typename,
                  total: pre.getMyNotifications.total,
                  data: [
                    ...pre.getMyNotifications.data,
                    ...fetchMoreResult.getMyNotifications.data,
                  ],
                },
              };
        },
      })
        .then(() => {
          reslove();
        })
        .catch(() => {
          reject();
        });
    });
  };

  return (
    <Page
      rightButton={useMemo(
        () => hasUnread && (
        <IconButton
              onClick={markAllRead}
              style={styles.iconButton}
              size='large'
        >
          <DoneAllIcon color='#FFF' />
        </IconButton>
          ),
        [hasUnread],
      )}
      title='Notifications'
    >
      <Row>
        <Col main>
          <InfiniteScrollApp
            mt={1}
            isFirstLoad={isFirstLoad}
            handleFetchMore={handleFetchMore}
            isEmpty={data?.getMyNotifications?.data?.length === 0}
            emptyTitle='You do not have any notifications'
            emptyText=''
          >
            <Card>
              <List style={styles.list}>
                {data?.getMyNotifications?.data?.map((list, index) => (
                  <Fragment key={list.id}>
                    <Notification
                      handleRead={handleReadSignle}
                      notification={list}
                      viewer={profileData?.getProfile}
                    />
                    {index + 1 < data.getMyNotifications.data.length && (
                      <Divider style={styles.divider} />
                    )}
                  </Fragment>
                ))}
              </List>
            </Card>
          </InfiniteScrollApp>
        </Col>
        <Col meta>
          {useMemo(
            () => (
              <PageMenu>
                <PageMenuItem
                  disabled={!hasUnread}
                  leftIcon={<DoneAllIcon />}
                  onClick={markAllRead}
                  primaryText='Mark All Read'
                />
              </PageMenu>
            ),
            [hasUnread],
          )}
        </Col>
      </Row>
    </Page>
  );
};

export default Notifications;
